/**
 * useChartQuotaAndShares - Shared hook for chart quota and short links management
 * Extracts duplicated fetchChartQuota logic from ChartInfoSection and AstronomicalSection
 */

import { devWarn, devError } from '../utils/devLog';
import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { JatakaResponse } from '../models/api';
import { useAuth } from '../contexts/AuthContext';
import { shortLinkApiService } from '../services/ShortLinkApiService';
import { buildChartMetadataFromResponse, parseDateTimeFromBasicInfo } from '../utils/shareHelpers';
import { globalEventStream, createEvent } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';

interface ChartQuota {
    active_count: number;
    max_allowed: number;
}

interface UseChartQuotaAndSharesOptions {
    apiResponse?: JatakaResponse | null;
    shareMode: boolean;
    /** Source identifier for emitted events */
    eventSource: string;
}

interface UseChartQuotaAndSharesResult {
    chartQuota: ChartQuota | null;
    backendShares: any[];
    loadingQuota: boolean;
    fetchChartQuota: () => Promise<void>;
    handleDeleteShortLink: (code: string) => Promise<void>;
}

export function useChartQuotaAndShares({
    apiResponse,
    shareMode,
    eventSource,
}: UseChartQuotaAndSharesOptions): UseChartQuotaAndSharesResult {
    const { isAuthenticated } = useAuth();
    const toast = useToast();

    const [chartQuota, setChartQuota] = useState<ChartQuota | null>(null);
    const [backendShares, setBackendShares] = useState<any[]>([]);
    const [loadingQuota, setLoadingQuota] = useState(false);

    // Fetch per-chart quota and shares list from backend
    const fetchChartQuota = useCallback(async () => {
        if (!apiResponse) {
            setChartQuota({ active_count: 0, max_allowed: 3 });
            setBackendShares([]);
            return;
        }

        try {
            setLoadingQuota(true);
            const basicInfo = apiResponse.basic_info;
            const datetime = parseDateTimeFromBasicInfo(basicInfo.date, basicInfo.time);
            const chartMetadata = buildChartMetadataFromResponse(
                apiResponse,
                basicInfo.chart_name || basicInfo.place_name
            );

            const response = await shortLinkApiService.getMyShares({
                datetime,
                latitude: basicInfo.latitudeValue,
                longitude: basicInfo.longitudeValue,
                timezoneOffset: chartMetadata.timezone_offset,
                altitude: parseFloat(basicInfo.altitude) || 0
            });

            setChartQuota({
                active_count: response.active_count,
                max_allowed: response.max_allowed
            });
            setBackendShares(response.charts || []);
        } catch (error) {
            devWarn('Failed to fetch chart quota:', error);
            setChartQuota({ active_count: 0, max_allowed: 3 });
            setBackendShares([]);
        } finally {
            setLoadingQuota(false);
        }
    }, [apiResponse]);

    // Fetch quota on mount (only if not in shareMode and authenticated)
    useEffect(() => {
        if (!shareMode && isAuthenticated) {
            fetchChartQuota();
        }
    }, [shareMode, isAuthenticated, apiResponse, fetchChartQuota]);

    // Re-fetch when chart data updates (link created/deleted)
    useEffect(() => {
        const subscription = globalEventStream.subscribe('chart.data.updated', (event) => {
            if (event.metadata && event.metadata.newShare) {
                const newShare = event.metadata.newShare;
                setBackendShares(prev => [...prev, newShare]);
                setChartQuota(prev => prev ? {
                    active_count: prev.active_count + 1,
                    max_allowed: prev.max_allowed
                } : prev);
            } else {
                if (!shareMode && isAuthenticated) {
                    fetchChartQuota();
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [shareMode, isAuthenticated, fetchChartQuota]);

    // Handle delete short link
    const handleDeleteShortLink = useCallback(async (code: string) => {
        try {
            await shortLinkApiService.deleteSharedChart(code);

            // Remove from chart metadata
            const multipleChartsContext = (window as any).multipleChartsContext;
            if (multipleChartsContext && multipleChartsContext.activeChartId) {
                const activeChart = multipleChartsContext.getActiveChart();
                if (activeChart && activeChart.metadata && activeChart.metadata.shortLinks) {
                    const updatedShortLinks = activeChart.metadata.shortLinks.filter((c: string) => c !== code);
                    multipleChartsContext.updateChart(multipleChartsContext.activeChartId, {
                        metadata: {
                            ...activeChart.metadata,
                            shortLinks: updatedShortLinks
                        }
                    });
                }
            }

            await fetchChartQuota();
            globalEventStream.emit(createEvent('chart.data.updated', eventSource, {}));

            toast({
                title: 'Link deleted',
                description: 'Short link has been deleted successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            devError('Failed to delete short link:', error);
            toast({
                title: 'Failed to delete',
                description: error instanceof Error ? error.message : 'Failed to delete short link',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            throw error;
        }
    }, [fetchChartQuota, eventSource, toast]);

    return {
        chartQuota,
        backendShares,
        loadingQuota,
        fetchChartQuota,
        handleDeleteShortLink,
    };
}
